/** Hide internal actor/user ids from history UI — show only human-readable names. */
export function formatHistoryActorLabel(actorName: string): string | null {
  const trimmed = (actorName || "").trim();
  if (!trimmed || trimmed === "—") return null;
  if (/^line_/i.test(trimmed)) return null;
  if (/^cm[a-z0-9]{8,}$/i.test(trimmed)) return null;
  if (/^user_/i.test(trimmed)) return null;
  if (/^[0-9A-Za-z]{20,}$/.test(trimmed) && !/[ก-๙\s]/.test(trimmed)) return null;
  return trimmed;
}

/** Drop internal ids from expandable history details. */
export function filterHistoryDetailsForDisplay(
  details: string[] | undefined,
): string[] {
  if (!details?.length) return [];
  return details.filter((line) => {
    const trimmed = line.trim();
    if (!trimmed) return false;
    if (/^confirm:/i.test(trimmed)) return false;
    if (/^id:/i.test(trimmed)) return false;
    return true;
  });
}

const RECEIVABLE_STATUS_LABELS: Record<string, string> = {
  open: "ยังไม่ได้รับชำระ",
  partially_paid: "รับชำระแล้วบางส่วน",
  closed: "ปิดยอดแล้ว",
  unallocated: "ยังไม่ได้จัดสรร",
  partially_allocated: "จัดสรรแล้วบางส่วน",
  fully_allocated: "จัดสรรครบแล้ว",
};

export function formatReceivableStatusLabel(status: string): string {
  const trimmed = status.trim();
  if (!trimmed) return trimmed;
  const key = trimmed.toLowerCase().replace(/\s+/g, "_");
  return RECEIVABLE_STATUS_LABELS[key] ?? trimmed;
}

function splitHistoryStatusValues(value: string): string[] {
  const parts = value
    .split(/[•·|,]/g)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.length ? parts : [value.trim()].filter(Boolean);
}

/** Parse history detail lines like `สถานะ: ค้าง` or `สถานะยอดค้าง: partially_paid`. */
export function parseHistoryStatusDetailLine(
  line: string,
): { prefix: string; statuses: string[] } | null {
  const trimmed = line.trim();
  const match = trimmed.match(/^(สถานะ(?:ยอดค้าง)?)\s*:\s*(.+)$/);
  if (!match) return null;

  const value = (match[2] ?? "").trim();
  if (!value) return null;

  const statuses = splitHistoryStatusValues(value).map(formatReceivableStatusLabel);
  return {
    prefix: match[1],
    statuses: statuses.length ? statuses : [formatReceivableStatusLabel(value)],
  };
}

/** Income/expense mirror titles that duplicate real money actions in the drawer. */
const ARTIFACT_HISTORY_TITLES = new Set([
  "อัปเดตรายรับ",
  "อัปเดตยอดจับคู่",
  "บันทึกรายรับ",
]);

function entryLooksLikeMoneyAction(entry: {
  title?: string;
  description?: string;
  type?: string;
}): boolean {
  const title = (entry.title || "").trim();
  const description = (entry.description || "").trim();
  const type = (entry.type || "").trim();
  if (
    [
      "matched",
      "unmatched",
      "moved",
      "split",
      "undone",
      "opened",
      "recorded",
    ].includes(type) &&
    !ARTIFACT_HISTORY_TITLES.has(title)
  ) {
    // "recorded" alone is ambiguous (could be create_income); require money copy.
    if (type === "recorded") {
      return (
        title === "ลงทะเบียนสลิป" ||
        description.includes("ลงทะเบียนสลิป") ||
        description.includes("จับคู่ยอดค้าง")
      );
    }
    return true;
  }
  return (
    title === "ลงทะเบียนสลิป" ||
    title.startsWith("จับคู่") ||
    title === "ยกเลิกจับคู่" ||
    title === "ย้ายยอดที่จับคู่" ||
    title === "จัดสรรใหม่" ||
    title === "เปิดยอดค้าง" ||
    title === "แก้ไขยอดค้าง" ||
    title === "ย้อนกลับการแก้ไข" ||
    description.includes("จับคู่ยอดค้าง") ||
    description.includes("ลงทะเบียนสลิป")
  );
}

/**
 * Drop bare รายรับ mirror rows when the timeline already has real money actions.
 * Backend should already filter; this keeps drawer modals clean if an old API responds.
 */
export function filterHistoryEntriesForDisplay<
  T extends { title?: string; description?: string; type?: string },
>(entries: T[]): T[] {
  if (!entries.length) return entries;
  const hasMoney = entries.some((entry) => entryLooksLikeMoneyAction(entry));
  return entries.filter((entry) => {
    const title = (entry.title || "").trim();
    const description = (entry.description || "").trim();
    if (!ARTIFACT_HISTORY_TITLES.has(title)) return true;
    if (hasMoney) return false;
    return Boolean(description && description !== title);
  });
}
